/**
 * Calendar data fetching hook with flexible date ranges.
 *
 * Fetches event-kind entities (meetings, custom event types) via the
 * `entity_kind: 'event'` query — dropping any reminder it returns — plus
 * reminders via a dedicated `ahoy_reminder`
 * query with no server-side owner scoping (mirrors the dashboard's
 * todayRemindersQueryOptions) — ownership is enforced client-side by
 * isReminderVisibleToUser (unowned OR mine). Reminder-specific features
 * (subject navigation, type filter) are handled via the CalendarEvent overlay
 * in eventMapper.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { useMyIntegrationEntityIds } from '@/hooks/integrations/useMyIntegrationEntityIds';
import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { usePermissions } from '@/hooks/usePermissions';
import { useReminderAutomationEnabled } from '@/hooks/useReminderAutomationEnabled';
import type {
  FilterCondition,
  FilterConditionGroup,
} from '@/lib/crm/filters/types';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';
import {
  crmObjectListSentinelQuery,
  useStoreBackedList,
} from '@/lib/crm/store';
import { logger } from '@/lib/logger';
import {
  CALENDAR_EVENT_ASSOCIATIONS,
  CALENDAR_REMINDER_ASSOCIATIONS,
} from '@/lib/meetings/calendarAssociations';
import { applyMineScopingNested } from '@/lib/meetings/mineScopingFilters';
import { calendarDateIn } from '@/lib/time';
import type {
  CrmObject,
  EntityTypeName,
  FilterGroup,
  PropertyFilter,
} from '@/types/crm';
import { convertConditionGroupsToFilterGroups } from '@/utils/filterUtils';

import {
  addCalendarDays,
  addCalendarMonths,
  calendarDayOf,
  calendarDayOfWeek,
  daysInCalendarMonth,
  startOfCalendarMonth,
  startOfCalendarWeek,
} from '../utils/calendarDays';
import type { CalendarEvent } from '../utils/eventMapper';
import {
  filterCalendarEntities,
  mapCrmObjectsToCalendarEvents,
  mapCrmTasksToCalendarEvents,
} from '../utils/eventMapper';
import { isReminderVisibleToUser } from '../utils/reminderUtils';
import {
  isTaskVisibleToUser,
  TASK_OWNER_ASSOCIATION_TYPE_NAME,
} from '../utils/taskUtils';

// ============================================================================
// Types
// ============================================================================

export interface ReminderEntityType {
  name: string;
  label: string;
}

export interface CalendarDataResult {
  events: CalendarEvent[];
  isLoading: boolean;
  /** True when showing cached data from a previous date range while new data loads */
  isPlaceholderData: boolean;
  error: unknown;
  /** Unique subject entity types present in the current reminders data. Used to
   * populate the filter sidebar checkboxes. */
  reminderEntityTypes: ReminderEntityType[];
}

// ============================================================================
// Date Range Helpers
// ============================================================================

/**
 * Get the first day of the week based on browser locale.
 * Returns 0 for Sunday, 1 for Monday, etc. (JS Date format).
 */
export function getWeekStartDay(): number {
  try {
    const locale = new Intl.Locale(navigator.language);
    // getWeekInfo() returns firstDay: 1=Monday...7=Sunday
    // Convert to JS Date format: 0=Sunday...6=Saturday
    const info = (
      locale as Intl.Locale & { getWeekInfo?: () => { firstDay?: number } }
    ).getWeekInfo?.();
    if (info?.firstDay) {
      return info.firstDay === 7 ? 0 : info.firstDay;
    }
  } catch {
    // Browser may not support getWeekInfo
  }
  return 0; // Default to Sunday for US
}

/**
 * Fetch-range helpers.
 *
 * Each returns the half-open instant range `[startDate, endDate)` the view
 * covers, with both boundaries at MIDNIGHT IN THE PROFILE TIMEZONE (see
 * `utils/calendarDays.ts`). That is the whole point of the timezone argument:
 * a browser-local boundary fetches the wrong 24 hours for anyone whose profile
 * timezone isn't their laptop's, and the grid then has nothing to render in
 * the cells it is about to draw.
 */

/**
 * Get the start and end of a day.
 */
export function getDayRange(
  date: Date,
  timezone: string
): { startDate: Date; endDate: Date } {
  const startDate = calendarDayOf(date, timezone);
  return { startDate, endDate: addCalendarDays(startDate, 1, timezone) };
}

/**
 * Get the start and end of a week containing the given date.
 */
export function getWeekRange(
  date: Date,
  timezone: string
): { startDate: Date; endDate: Date } {
  const startDate = startOfCalendarWeek(date, getWeekStartDay(), timezone);
  return { startDate, endDate: addCalendarDays(startDate, 7, timezone) };
}

/**
 * Get the start and end of a month containing the given date.
 */
export function getMonthRange(
  date: Date,
  timezone: string
): { startDate: Date; endDate: Date } {
  return {
    startDate: startOfCalendarMonth(date, timezone),
    endDate: addCalendarMonths(date, 1, timezone),
  };
}

/**
 * Get the range covering the full visible month-view GRID — including the
 * leading days from the previous month and the trailing days from the
 * next month that share weeks with the current month. The MonthView grid
 * always renders complete weeks, so events on those leading/trailing
 * days need to be fetched and shown alongside current-month events.
 *
 * Mirrors the grid-cell computation in `monthGridDays` (utils/calendarDays.ts),
 * which MonthView renders from — the two must cover the same span.
 */
export function getMonthGridRange(
  date: Date,
  timezone: string
): {
  startDate: Date;
  endDate: Date;
} {
  const weekStartDay = getWeekStartDay();
  const firstOfMonth = startOfCalendarMonth(date, timezone);

  // Days BEFORE the first of the month that share its starting week.
  const daysBeforeMonth =
    (calendarDayOfWeek(firstOfMonth, timezone) - weekStartDay + 7) % 7;
  const startDate = addCalendarDays(firstOfMonth, -daysBeforeMonth, timezone);

  // Total cells in the grid, rounded up to full weeks.
  const totalCells =
    Math.ceil((daysBeforeMonth + daysInCalendarMonth(date, timezone)) / 7) * 7;

  // Exclusive end (one day after the last visible cell).
  return {
    startDate,
    endDate: addCalendarDays(startDate, totalCells, timezone),
  };
}

// ============================================================================
// Local Date Helper
// ============================================================================

/**
 * Page size for the broad event-kind query. The select-step post-filter drops
 * cancelled and non-meeting entities client-side; pagination is widened so
 * those drops cannot push valid meetings off the visible page.
 */
const CALENDAR_PAGE_LIMIT = 500;

/**
 * Drop reminders from the event-kind query's results.
 *
 * `ahoy_reminder` is an event-kind type, so the `entity_kind: 'event'` query
 * can return one whenever a scoping arm matches it. Today neither meeting arm
 * does, but that exclusion is incidental. Reminders must reach the calendar
 * through exactly one path — the dedicated `ahoy_reminder` query, where
 * `isReminderVisibleToUser` enforces ownership — so a future arm change can't
 * let another user's reminder (or a duplicate of a visible one) in unfiltered.
 */
function excludeReminders(objects: CrmObject[]): CrmObject[] {
  return objects.filter(obj => obj.entity_type_name !== 'ahoy_reminder');
}

/**
 * Association projection for the calendar's task query.
 *
 * The calendar reads exactly one task association — the owner edge, for
 * `isTaskVisibleToUser` narrowing (the task mapper reads properties only). It
 * deliberately does NOT reuse `DASHBOARD_TASK_ASSOCIATIONS`, which also requests
 * the stage edge for dashboard completion-status: the calendar renders no
 * completion status, so requesting the stage edge would add an
 * association-target fan-out per task to the calendar's largest list query and
 * couple it to future dashboard-only projection additions. Owner narrowing
 * needs the edge only, not the target's properties (see the `properties` NOTE
 * in lib/dashboard/queries.ts).
 */
const CALENDAR_TASK_ASSOCIATIONS: Record<string, { properties: string[] }> = {
  [TASK_OWNER_ASSOCIATION_TYPE_NAME]: { properties: [] },
};

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Hook to fetch calendar events for any date range.
 */
export function useCalendarData(
  startDate: Date,
  endDate: Date,
  orgId: number | undefined,
  options?: {
    /** Server-side text search on the event-kind (meetings) query ONLY.
     * Reminders and tasks come from their own queries and are not narrowed by
     * it. No caller passes it today (CalendarPage searches via
     * CalendarSearchBar); extend it to the reminder query before a caller
     * relies on it for reminders. */
    search?: string;
    /** Advanced filters ANDed into the event-kind (meetings) query ONLY —
     * same scope as `search`: reminders and tasks ignore them. */
    filterConditions?: FilterCondition[];
    /** When null, all reminders are shown. When a Set, only reminders whose
     * subjectEntityType is in the set are shown. Meetings are unaffected. */
    enabledReminderTypes?: Set<string> | null;
  }
): CalendarDataResult {
  // Get current user's entity ID to filter events in post-processing
  const { entityId, isLoading: isEntityLoading } = useCurrentUserEntity();

  // The date-only filters below (`ahoy_start_date` / `ahoy_end_date`) are
  // calendar days, so they must be the profile timezone's days — the same
  // zone the caller built `startDate`/`endDate` in. Read here rather than
  // taken as an argument so the two can never be threaded from different
  // sources; the range Dates and these strings then always name one day.
  const timezone = useDisplayTimezone();

  const {
    integrationEntityIds,
    isLoaded: areIntegrationsLoaded,
    error: integrationError,
  } = useMyIntegrationEntityIds(orgId);

  useEffect(() => {
    if (integrationError) {
      logger.error(
        'Failed to load integration entity IDs',
        integrationError as Error,
        { orgId }
      );
      logger.warn(
        'Integration lookup failed, falling back to attendee-only meetings',
        { orgId }
      );
    }
  }, [integrationError, orgId]);

  // Build 8 base date-overlap groups (Cases 1-8 below), then collapse them
  // with the 2 positive scoping arms from buildMineScopingArms (integration,
  // attendee) into a single nested group: `(date1 OR … OR date8) AND (arm1 OR
  // arm2) AND advancedFilters`. Reminders are fetched via a separate
  // dedicated `ahoy_reminder` query below (remindersQuery) with no
  // server-side owner scoping — see that query's comment. The arms are
  // load-bearing on their own, not redundant with each other: a solo
  // calendar block (focus time, lunch) has no attendees, so it carries no
  // `ahoy_meeting_to_user` edge and is only reachable via the integration
  // arm; a meeting synced by a colleague's calendar carries their
  // integration, not the viewer's, so it's only reachable via the attendee
  // arm. Cancelled-meeting drop and non-meeting passthrough still happen in
  // select via filterCalendarEntities. CALENDAR_PAGE_LIMIT remains high so
  // the cancelled-meeting drop does not push valid meetings off the page in
  // dense calendars.
  const filterGroups: FilterGroup[] = useMemo(() => {
    const rangeStartIso = startDate.toISOString();
    const rangeEndIso = endDate.toISOString();
    const rangeStartDate = calendarDateIn(startDate, timezone);
    const rangeEndDate = calendarDateIn(endDate, timezone);

    const advancedFilterGroup: FilterConditionGroup = {
      id: 'calendar-advanced-filters',
      conditions: options?.filterConditions ?? [],
    };
    const advancedFilters = convertConditionGroupsToFilterGroups([
      advancedFilterGroup,
    ]).filter_groups.flatMap(g => g.filters);

    // Pure date groups (without advancedFilters), collapsed into a single
    // nested group via applyMineScopingNested — 1 filter group instead of the
    // pre-#13229 24-group cross-product.
    const pureDateGroups: FilterGroup[] = [
      // Group 1: timed events overlapping the range (Cases 1, 2)
      {
        filters: [
          {
            property: 'ahoy_end_time',
            operator: 'gt',
            value: rangeStartIso,
          } as PropertyFilter,
          {
            property: 'ahoy_start_time',
            operator: 'lt',
            value: rangeEndIso,
          } as PropertyFilter,
        ],
      },
      // Group 2: all-day events with both start and end dates (Case 3) overlapping the window
      {
        filters: [
          {
            property: 'ahoy_end_date',
            operator: 'gte',
            value: rangeStartDate,
          } as PropertyFilter,
          {
            property: 'ahoy_start_date',
            operator: 'lt',
            value: rangeEndDate,
          } as PropertyFilter,
        ],
      },
      // Group 3: hybrid — timed start, all-day end (Case 2)
      {
        filters: [
          {
            property: 'ahoy_end_date',
            operator: 'gte',
            value: rangeStartDate,
          } as PropertyFilter,
          {
            property: 'ahoy_start_time',
            operator: 'lt',
            value: rangeEndIso,
          } as PropertyFilter,
        ],
      },
      // Group 4: hybrid — all-day start, timed end (Case 4)
      {
        filters: [
          {
            property: 'ahoy_end_time',
            operator: 'gt',
            value: rangeStartIso,
          } as PropertyFilter,
          {
            property: 'ahoy_start_date',
            operator: 'lt',
            value: rangeEndDate,
          } as PropertyFilter,
        ],
      },
      // Group 5: start-only timed events (Case 5)
      {
        filters: [
          {
            property: 'ahoy_start_time',
            operator: 'gte',
            value: rangeStartIso,
          } as PropertyFilter,
          {
            property: 'ahoy_start_time',
            operator: 'lt',
            value: rangeEndIso,
          } as PropertyFilter,
        ],
      },
      // Group 6: start-only all-day events (Case 7)
      {
        filters: [
          {
            property: 'ahoy_start_date',
            operator: 'gte',
            value: rangeStartDate,
          } as PropertyFilter,
          {
            property: 'ahoy_start_date',
            operator: 'lt',
            value: rangeEndDate,
          } as PropertyFilter,
        ],
      },
      // Group 7: end-only all-day events (Case 8)
      {
        filters: [
          {
            property: 'ahoy_end_date',
            operator: 'gte',
            value: rangeStartDate,
          } as PropertyFilter,
          {
            property: 'ahoy_end_date',
            operator: 'lt',
            value: rangeEndDate,
          } as PropertyFilter,
        ],
      },
      // Group 8: end-only timed events (Case 6)
      {
        filters: [
          {
            property: 'ahoy_end_time',
            operator: 'gte',
            value: rangeStartIso,
          } as PropertyFilter,
          {
            property: 'ahoy_end_time',
            operator: 'lt',
            value: rangeEndIso,
          } as PropertyFilter,
        ],
      },
    ];
    // Scope to meetings that are mine via the shared positive scoping arms.
    // The arms replaced a single `ahoy_meeting_to_integration in_or_isnull
    // [...]` filter, whose absence arm meant "no integration edge I can SEE"
    // once association-target gating landed — so every colleague's org-visible
    // meeting matched it (#13192; arms stay positive `in`, never an absence
    // test — see mineScopingFilters.ts). This query is entity_kind 'event',
    // which DOES include `ahoy_reminder` (it is default + event kind); the
    // meeting-only arms happen not to match reminders, and `events` below
    // additionally drops any reminder this query returns (excludeReminders),
    // so reminders reach the calendar only via the dedicated remindersQuery.
    return applyMineScopingNested(
      pureDateGroups,
      integrationEntityIds,
      entityId,
      advancedFilters
    );
  }, [
    startDate,
    endDate,
    timezone,
    options?.filterConditions,
    integrationEntityIds,
    entityId,
  ]);

  const calendarQuery = useQuery({
    ...crmObjectListSentinelQuery(
      orgId ?? 0,
      { entity_kind: 'event' },
      {
        filter_groups: filterGroups,
        sort: 'ahoy_start_time',
        order: 'asc',
        search: options?.search || undefined,
        include_total_count: false,
        limit: CALENDAR_PAGE_LIMIT,
      },
      // Projection, not `include_associated_objects: true` — see
      // lib/meetings/calendarAssociations.ts. All-types made the backend gate and
      // hydrate every association type in the org on every calendar load.
      { include_associations: CALENDAR_EVENT_ASSOCIATIONS }
    ),
    enabled: !!orgId && !!entityId && areIntegrationsLoaded,
    staleTime: 60_000, // 1 minute
    gcTime: 5 * 60_000, // 5 minutes
    // Keep previous date range's events visible while new data loads
    placeholderData: keepPreviousData,
  });
  const list = useStoreBackedList(calendarQuery);

  const remindersEnabled = useReminderAutomationEnabled(orgId);

  // Reminders are fetched via a dedicated `ahoy_reminder` query with NO
  // server-side owner scoping — every reminder in the date range is fetched
  // regardless of owner, and ownership is enforced client-side by
  // isReminderVisibleToUser (unowned OR mine). This mirrors the dashboard's
  // todayRemindersQueryOptions. Reminders returned by the event-kind query
  // above are dropped (excludeReminders) — a dedicated query is the only way
  // to show ownerless reminders without reopening the #13192 org-wide meeting
  // leak: an `in_or_isnull` owner arm on the event-kind query would match
  // every meeting too (see mineScopingFilters.ts). Unlike the event-kind
  // query, this one does NOT apply `options.search`/`filterConditions`.
  const reminderFilterGroups: FilterGroup[] = useMemo(
    () => [
      {
        filters: [
          {
            property: 'ahoy_start_date',
            operator: 'gte',
            value: calendarDateIn(startDate, timezone),
          } as PropertyFilter,
          {
            property: 'ahoy_start_date',
            operator: 'lt',
            value: calendarDateIn(endDate, timezone),
          } as PropertyFilter,
        ],
      },
    ],
    [startDate, endDate, timezone]
  );

  const remindersQuery = useQuery({
    ...crmObjectListSentinelQuery(
      orgId ?? 0,
      'ahoy_reminder' as EntityTypeName,
      {
        filter_groups: reminderFilterGroups,
        sort: 'ahoy_start_date',
        order: 'asc',
        include_total_count: false,
        limit: CALENDAR_PAGE_LIMIT,
      },
      { include_associations: CALENDAR_REMINDER_ASSOCIATIONS }
    ),
    enabled: !!orgId && !!entityId && remindersEnabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
  const remindersList = useStoreBackedList(remindersQuery);

  const { hasPermission } = usePermissions();
  const canReadTasks = hasPermission('ahoy_task');

  // Tasks are not `event`-kind and cannot be made so, so they need their own
  // query merged into the same list. Query both ahoy_datetime (timed) and
  // ahoy_date (date-only) since ahoy_date may or may not be kept in sync with
  // ahoy_datetime depending on cascade wiring.
  const taskFilterGroups: FilterGroup[] = useMemo(() => {
    const rangeStartIso = startDate.toISOString();
    const rangeEndIso = endDate.toISOString();
    const rangeStartDate = calendarDateIn(startDate, timezone);
    const rangeEndDate = calendarDateIn(endDate, timezone);

    return [
      {
        filters: [
          {
            property: 'ahoy_datetime',
            operator: 'gte',
            value: rangeStartIso,
          } as PropertyFilter,
          {
            property: 'ahoy_datetime',
            operator: 'lt',
            value: rangeEndIso,
          } as PropertyFilter,
        ],
      },
      {
        filters: [
          {
            property: 'ahoy_date',
            operator: 'gte',
            value: rangeStartDate,
          } as PropertyFilter,
          {
            property: 'ahoy_date',
            operator: 'lt',
            value: rangeEndDate,
          } as PropertyFilter,
        ],
      },
    ];
  }, [startDate, endDate, timezone]);

  const tasksQuery = useQuery({
    ...crmObjectListSentinelQuery(
      orgId ?? 0,
      'ahoy_task' as EntityTypeName,
      {
        filter_groups: taskFilterGroups,
        sort: 'ahoy_datetime',
        order: 'asc',
        include_total_count: false,
        limit: CALENDAR_PAGE_LIMIT,
      },
      { include_associations: CALENDAR_TASK_ASSOCIATIONS }
    ),
    enabled: !!orgId && canReadTasks,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
  const taskList = useStoreBackedList(tasksQuery);

  const objectTypesQuery = useObjectTypes(orgId);

  const entityTypeLabelLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of objectTypesQuery.data?.allObjectTypes ?? []) {
      map.set(t.entity_type_name, t.label);
    }
    return (entityTypeName: string) => map.get(entityTypeName);
  }, [objectTypesQuery.data?.allObjectTypes]);

  // Extract unique subject entity types from reminders for the filter UI.
  // Hidden when the automation is disabled.
  const reminderEntityTypes = useMemo((): ReminderEntityType[] => {
    if (!remindersEnabled) {
      return [];
    }
    const types = new Map<string, string>();
    for (const obj of remindersList.results) {
      // Only surface filter options for reminders the user can see (mirrors the
      // ownership filter applied to the rendered events).
      if (!isReminderVisibleToUser(obj, entityId)) {
        continue;
      }
      const assoc = obj.associations?.find(
        a => a.association_type_name === 'ahoy_reminder_to_subject'
      );
      const subject = assoc
        ? remindersList.associatedObjects[assoc.target_entity_id]
        : undefined;
      if (subject?.entity_type_name) {
        types.set(
          subject.entity_type_name,
          entityTypeLabelLookup(subject.entity_type_name) ??
            subject.entity_type_name
        );
      }
    }
    return Array.from(types.entries()).map(([name, label]) => ({
      name,
      label,
    }));
  }, [
    remindersList.results,
    remindersList.associatedObjects,
    remindersEnabled,
    entityTypeLabelLookup,
    entityId,
  ]);

  const events = useMemo(() => {
    // Map meetings and other event-kind entities through the unified mapper.
    // Reminders are dropped here (see excludeReminders) so their only path is
    // the dedicated query below, which applies the ownership filter.
    const meetingEvents = mapCrmObjectsToCalendarEvents(
      excludeReminders(filterCalendarEntities(list.results)),
      list.associatedObjects
    );

    // Reminders come from the dedicated ahoy_reminder query, with no
    // server-side owner scoping — keep only reminders owned by the current
    // user or unowned (isReminderVisibleToUser), then map through the same
    // mapper with the reminder query's own associatedObjects.
    const visibleReminders = remindersEnabled
      ? remindersList.results.filter(obj =>
          isReminderVisibleToUser(obj, entityId)
        )
      : [];
    const reminderEvents = mapCrmObjectsToCalendarEvents(
      visibleReminders,
      remindersList.associatedObjects
    );

    // Apply the reminder type filter — when enabledReminderTypes is a Set,
    // drop reminders whose subject entity type is not in the set.
    const enabledReminderTypes = options?.enabledReminderTypes;
    const reminderEventsAfterTypeFilter =
      enabledReminderTypes == null
        ? reminderEvents
        : reminderEvents.filter(e =>
            enabledReminderTypes.has(e.subjectEntityType ?? '')
          );

    // Mirrors the reminder narrowing above: a declutter filter over
    // already-authorized data, not an access-control gate.
    const taskEvents = canReadTasks
      ? mapCrmTasksToCalendarEvents(
          taskList.results.filter(t => isTaskVisibleToUser(t, entityId))
        )
      : [];

    return [...meetingEvents, ...reminderEventsAfterTypeFilter, ...taskEvents];
  }, [
    list.results,
    list.associatedObjects,
    remindersList.results,
    remindersList.associatedObjects,
    remindersEnabled,
    options?.enabledReminderTypes,
    entityId,
    taskList.results,
    canReadTasks,
  ]);

  return {
    events,
    // Treat "integrations still loading" as loading too. The meetings query is
    // gated on `areIntegrationsLoaded`, so without this the hook would briefly
    // report ready (isLoading=false, events=[]) before the first fetch even
    // starts, letting the calendar flash an empty/ready UI. Reminder loading
    // is OR'd in too, so a reminders-only org doesn't briefly report ready
    // before the dedicated reminder query resolves.
    isLoading:
      list.isLoading ||
      isEntityLoading ||
      !areIntegrationsLoaded ||
      (canReadTasks && taskList.isLoading) ||
      (remindersEnabled && remindersList.isLoading),
    // The reminder query's own error is intentionally NOT merged into the
    // returned error (matches the dashboard) — a reminder-overlay failure
    // must not fail the whole calendar.
    isPlaceholderData:
      list.isPlaceholderData || remindersList.isPlaceholderData,
    error: list.error ?? null,
    reminderEntityTypes,
  };
}
